import { FC, useMemo } from "react";

import { CreateGMWTask, EditGMWTask, MonitoringTube } from "@nens/brostar/uploadapi-client";

import { getApi } from "@/api/api-configuration";
import { getFormObjectValues, initializeFormEditingState } from "@/helpers/helpers";
import { parseNumber } from "@/helpers/parse-number";
import { useAfterFormSubmit } from "@/hooks/use-after-form-submit";
import { BrostarForm, useBrostarFormDataStorage } from "@/libs/brostar-form";
import { GmwFormProps } from "@/types/form-types";

import { useGmwUiSchema } from "../../schema/gmw-ui-schema";
import { validateGmwFormUnified } from "../../schema/gmw-validation";

import { MonitoringWellVisualization } from "./components/monitoring-well-visualisation";
import { getGmwSchema } from "./schema";

const GmwForm: FC<GmwFormProps> = (props) => {
  const api = getApi();
  const afterSubmit = useAfterFormSubmit();

  const { oldUploadTask, task, type } = getFormObjectValues(props);

  const { isEditing, getIsEditing } = initializeFormEditingState(props);

  const { formData, handleChange } = useBrostarFormDataStorage<CreateGMWTask | EditGMWTask>({
    key: "gmw",
    task,
    transformData: (formData) => {
      if (!formData) return formData;
      const monitoringTubes = formData.sourcedocumentData.monitoringTubes ?? [];
      return {
        ...formData,
        sourcedocumentData: {
          ...formData.sourcedocumentData,
          numberOfMonitoringTubes: monitoringTubes.length,
          monitoringTubes: monitoringTubes.map((tube) => ({
            ...tube,
            numberOfGeoOhmCables: tube.geoOhmCables?.length ?? 0,
          })),
        },
      };
    },
  });

  const uiSchema = useGmwUiSchema(formData?.metadata.qualityRegime);

  const schema = useMemo(() => getGmwSchema({ type, isEditing }), [type, isEditing]);

  const onSubmit = async () => {
    if (!formData) return;
    await (getIsEditing(formData) ? api.editGMW(formData) : api.createGMW(formData));

    // Remove the old upload task
    if (oldUploadTask?.uuid) api.apiUploadtasksDelete({ uuid: oldUploadTask.uuid });

    await afterSubmit();
  };

  const visualizationData = useMemo(() => {
    const monitoringTubes = formData?.sourcedocumentData?.monitoringTubes || [];

    return {
      wellHeadProtector: formData?.sourcedocumentData.wellHeadProtector ?? "",
      groundLevelPosition: formData?.sourcedocumentData.groundLevelPosition
        ? parseNumber(formData.sourcedocumentData.groundLevelPosition)
        : 0.0, // cast to number
      monitoringTubes: monitoringTubes
        ? monitoringTubes
            .filter((tube): tube is MonitoringTube => tube !== undefined)
            .map((tube) => ({
              tubeTopPosition: tube.tubeTopPosition ? parseNumber(tube.tubeTopPosition) : 0.0,
              plainTubePartLength: tube.plainTubePartLength
                ? parseNumber(tube.plainTubePartLength)
                : 0.0,
              screenLength: tube.screenLength ? parseNumber(tube.screenLength) : 0.0,
              sedimentSumpLength: tube.sedimentSumpLength
                ? parseNumber(tube.sedimentSumpLength)
                : 0.0,
            }))
        : [],
    };
  }, [formData?.sourcedocumentData]);

  return (
    <BrostarForm<CreateGMWTask | EditGMWTask>
      uploadTask={oldUploadTask}
      schema={schema}
      formData={formData}
      onChange={handleChange}
      uiSchema={{
        ...uiSchema,
        sourcedocumentData: {
          ...uiSchema.sourcedocumentData,
          "ui:addElement": <MonitoringWellVisualization wellData={visualizationData} />,
        },
      }}
      onSubmit={onSubmit}
      customValidate={validateGmwFormUnified}
    />
  );
};

export { GmwForm };
